import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import type { Language } from '../i18n/translations';
import { ChevronDown } from 'lucide-react';

interface LanguageOption {
  code: Language;
  name: string;
  flagUrl: string;
}

const languageOptions: LanguageOption[] = [
  {
    code: 'en',
    name: 'English',
    flagUrl: 'https://flagcdn.com/us.svg'
  },
  {
    code: 'fr',
    name: 'Français',
    flagUrl: 'https://flagcdn.com/fr.svg'
  }
];

export function LanguageSelector() {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentOption = languageOptions.find(opt => opt.code === language)!;

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="mb-2 text-sm font-medium text-gray-700">
        Select Language
      </div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-md bg-white hover:bg-gray-50 border border-gray-200 shadow-sm transition-colors w-full"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <img 
          src={currentOption.flagUrl} 
          alt={`${currentOption.name} flag`}
          className="w-5 h-4 object-cover rounded-sm"
        />
        <span className="text-sm font-medium text-gray-700">
          {currentOption.code.toUpperCase()}
        </span>
        <ChevronDown 
          className={`w-4 h-4 text-gray-500 transition-transform ml-auto ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-full rounded-md bg-white border border-gray-200 shadow-lg py-1">
          {languageOptions.map((option) => (
            <button
              key={option.code}
              onClick={() => {
                setLanguage(option.code);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                language === option.code
                  ? 'bg-blue-50 text-blue-700'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <img 
                src={option.flagUrl} 
                alt={`${option.name} flag`}
                className="w-5 h-4 object-cover rounded-sm"
              />
              <span className="font-medium">{option.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
} 